import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleSheet,
} from 'react-native';
import { openExternalUrl } from '../../utils/openExternalUrl';
import { Feather } from '@expo/vector-icons';
import { Ionicons } from '@expo/vector-icons';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaShadows } from '../../styles/theme';
import { useTranslation } from 'react-i18next';
import { formatCount, formatRating } from '../../utils/formatNumber';
import { getSourceFaviconUrl, getSourceDisplayName } from '../../utils/recipeSource';

interface SourceChipProps {
  sourceUrl: string;
  sourceName?: string;
  sourceRating?: number;
  sourceRatingCount?: number;
  /** Drops the chip's own vertical margins so a parent can own the spacing. */
  flush?: boolean;
}

export const SourceChip: React.FC<SourceChipProps> = ({
  sourceUrl,
  sourceName,
  sourceRating,
  sourceRatingCount,
  flush,
}) => {
  const { i18n } = useTranslation();
  const [faviconError, setFaviconError] = useState(false);

  const displayName = getSourceDisplayName(sourceUrl) || sourceName || 'Source';
  const faviconUrl = getSourceFaviconUrl(sourceUrl);
  const initials = displayName.slice(0, 2).toUpperCase();

  const handleOpen = async () => {
    try {
      await openExternalUrl(sourceUrl);
    } catch {}
  };

  return (
    <TouchableOpacity
      style={[styles.chip, flush && styles.chipFlush]}
      onPress={handleOpen}
      activeOpacity={0.75}
    >
      {faviconUrl && !faviconError ? (
        <Image
          source={{ uri: faviconUrl }}
          style={styles.favicon}
          resizeMode="contain"
          onError={() => setFaviconError(true)}
        />
      ) : (
        <View style={styles.initials}>
          <Text style={styles.initialsText}>{initials}</Text>
        </View>
      )}

      <View style={styles.textBlock}>
        <View style={styles.nameRow}>
          <Text style={styles.name} numberOfLines={1}>{displayName}</Text>
          <Feather name="external-link" size={12} color={Paprika.textMuted} style={styles.externalIcon} />
        </View>

        {sourceRating != null && (
          <View style={styles.ratingRow}>
            <Ionicons name="star" size={11} color="#F5C542" />
            <Text style={styles.ratingText}>
              {formatRating(sourceRating, i18n.language)}
              {sourceRatingCount != null ? ` (${formatCount(sourceRatingCount, i18n.language)})` : ''}
            </Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: Paprika.cardBg,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Paprika.border,
    paddingVertical: 8,
    paddingHorizontal: 12,
    gap: PaprikaSpacing.sm,
    marginTop: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.md,
    ...PaprikaShadows.card,
  },
  chipFlush: {
    marginTop: 0,
    marginBottom: 0,
  },
  favicon: {
    width: 22,
    height: 22,
    borderRadius: 6,
  },
  initials: {
    width: 22,
    height: 22,
    borderRadius: 6,
    backgroundColor: Paprika.secondaryContainer,
    alignItems: 'center',
    justifyContent: 'center',
  },
  initialsText: {
    ...PaprikaTypography.caption,
    color: Paprika.onSecondaryContainer,
    fontWeight: '600',
    fontSize: 9,
  },
  textBlock: {
    gap: 2,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  name: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
    maxWidth: 200,
  },
  externalIcon: {
    marginTop: 1,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  ratingText: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
  },
});

export default SourceChip;
